import { useRef, useState } from "react";
import { Button } from "@/shared/components/shadcn/Button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/shadcn/Dialog";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FormField } from "@/shared/components/ui/FormField";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { useUpdateClient } from "../hooks/clientQueries";
import type { Client } from "../types/clientTypes";

export function ClientNotes({ client }: { client: Client }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(client.notes || "");
  const [validationError, setValidationError] = useState<string>();
  const updateMutation = useUpdateClient(client.id);
  const editButton = useRef<HTMLButtonElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);

  const openEditor = () => {
    setDraft(client.notes || "");
    setValidationError(undefined);
    updateMutation.reset();
    setOpen(true);
  };

  return (
    <Section title="Notes" action={<SectionAction aria-label="Edit notes" onClick={(event) => { editButton.current = event.currentTarget; openEditor(); }}>Edit</SectionAction>}>
      <InfoRow
        title={client.notes ? <span className="whitespace-pre-wrap font-normal">{client.notes}</span> : "No notes added"}
        density="compact"
      />
      <Dialog open={open} onOpenChange={(nextOpen) => { if (!updateMutation.isPending) setOpen(nextOpen); }}>
        <DialogContent
          showCloseButton={false}
          aria-busy={updateMutation.isPending}
          onOpenAutoFocus={(event) => { event.preventDefault(); textarea.current?.focus(); }}
          onCloseAutoFocus={(event) => { event.preventDefault(); editButton.current?.focus(); }}
          onPointerDownOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => { if (updateMutation.isPending) event.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Edit notes</DialogTitle>
            <DialogDescription>Save notes to this customer without changing their contact details.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={(event) => {
            event.preventDefault();
            if (updateMutation.isPending) return;
            if (draft.trim().length > 2000) {
              setValidationError("Use 2,000 characters or fewer.");
              textarea.current?.focus();
              return;
            }
            updateMutation.mutate({ notes: draft.trim() }, { onSuccess: () => setOpen(false) });
          }}>
            <FormField id="customer-notes" label="Notes" optional error={validationError}>
              <TextArea ref={textarea} rows={6} maxLength={2000} value={draft} disabled={updateMutation.isPending} onChange={(event) => { setDraft(event.target.value); setValidationError(undefined); }} />
            </FormField>
            {updateMutation.error && <ErrorState message={updateMutation.error.message} />}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={updateMutation.isPending}>Close</Button>
              <Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? "Saving..." : "Save notes"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
